import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import Icon from '../../components/AppIcon';
import ResourceSearch from '../../components/ui/ResourceSearch';
import { blogService } from '../../services/blogService';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchResults = async () => {
      if (!query) {
        setResults([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await blogService.searchPosts(query);
        setResults(data || []);
      } catch (error) {
        console.error('Error fetching search results:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, [query]);

  const getPageIcon = (page) => {
    switch (page) {
      case 'seller-toolbox': return 'Store';
      case 'suppliers-resource': return 'Truck';
      case 'documentation': return 'BookOpen';
      case 'api-reference': return 'Code';
      case 'tutorials': return 'Video';
      case 'resources-hub': return 'Globe';
      default: return 'FileText';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-left">
      <Header />
      
      <div className="bg-slate-900 text-white pt-32 pb-16 border-b border-slate-800">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl">
            <h1 className="text-3xl md:text-4xl font-extrabold mb-8 tracking-tight">
              Search Results for <span className="text-blue-400">"{query}"</span>
            </h1>
            <ResourceSearch 
              placeholder="Search again..." 
              initialValue={query} 
              showDropdown={false} 
              autoFocus={false}
            />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12">
        <div className="flex justify-between items-center mb-8">
            <p className="text-slate-600">
                Found <span className="font-bold text-slate-900">{results.length}</span> {results.length === 1 ? 'article' : 'articles'} matching your search
            </p>
            <Link to="/resources-hub" className="text-blue-600 hover:text-blue-700 font-bold flex items-center group">
              Back to Hub
              <Icon name="ArrowRight" size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="animate-pulse bg-white rounded-2xl h-64 shadow-sm border border-slate-100" />
            ))}
          </div>
        ) : (
          <>
            {results.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {results.map((post) => (
                  <Link 
                    key={post.id} 
                    to={`/${post.target_page || post.targetPage || 'blog'}/${post.slug}`}
                    className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl transition-all group flex flex-col h-full"
                  >
                    <div className="h-40 bg-slate-100 overflow-hidden relative">
                      {post.featured_image ? (
                        <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                          <Icon name={getPageIcon(post.target_page || post.targetPage)} size={48} />
                        </div>
                      )}
                      <div className="absolute top-4 left-4">
                        <span className="px-2 py-1 bg-blue-600 text-white text-[10px] font-bold rounded-full uppercase tracking-wider shadow-sm">
                          {post.category?.name}
                        </span>
                      </div>
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                        {post.targetPage?.replace('-', ' ')}
                      </div>
                      <h3 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="mt-auto flex items-center text-blue-600 font-semibold text-sm">
                        Read Full Article
                        <Icon name="ArrowRight" size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="py-24 text-center bg-white rounded-2xl border border-dashed border-slate-200">
                <div className="inline-flex items-center justify-center p-6 bg-slate-50 rounded-full mb-6 text-slate-300">
                  <Icon name="Search" size={64} />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-2">No results found</h3>
                <p className="text-slate-500 max-w-md mx-auto mb-8">
                  We couldn't find any articles matching "{query}". Try different keywords or browse our categories.
                </p>
                <Button onClick={() => navigate('/resources-hub')}>Browse All Resources</Button>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default SearchResults;
